<template>
  <div>
    <div v-if="!secret">
      <span v-if="loading" class="text-muted">Generating secret...</span>
      <button v-else class="btn btn-outline-primary btn-sm" @click="generateSecret">Try again</button>
    </div>
    <div v-else>
      <p class="mb-1"><strong>Scan this with your authenticator app</strong></p>
      <p class="small text-muted">For example Google Authenticator, 1Password or Authy.</p>
      <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR code for the authenticator app" class="d-block mb-2">
      <p class="mb-1"><strong>Setup key</strong></p>
      <p class="text-monospace bg-light p-2 rounded">{{ secret.secretKey }}</p>
      <p class="small">
        Can't scan the code? Add the key to your authenticator app manually, or
        <a :href="otpUri">open it in the app</a> if one is installed on this device.
      </p>
      <div class="input-group">
        <input type="text" autocomplete="one-time-code" class="form-control" v-model="verificationCode" placeholder="Enter the 6-digit code">
        <div class="input-group-append">
          <button class="btn btn-primary" :disabled="loading || !verificationCode" @click="enroll">Verify Code</button>
        </div>
      </div>
      <p class="mt-1 small">After code verification, Multi-factor Authentication will be enabled, and you will be forced to authenticate again</p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import qrcode from 'qrcode-generator'
import { getAuth, multiFactor, TotpMultiFactorGenerator } from 'firebase/auth'

// Keeps devel and production entries apart in the user's authenticator app
const issuer = process.env.VUE_APP_FIREBASE_PROJECT_ID === 'enablebanking-devel' ? 'Enable Banking Dev' : 'Enable Banking'

export default {
  data() {
    return {
      secret: null,
      otpUri: '',
      qrDataUrl: '',
      verificationCode: '',
      loading: true
    }
  },
  computed: {
    ...mapGetters(['user'])
  },
  mounted() {
    this.generateSecret()
  },
  methods: {
    ...mapActions(['logout', 'pushAlertMessage']),
    // TOTP is modular-only, so these go through getAuth() rather than the compat user
    async generateSecret() {
      this.loading = true
      try {
        const currentUser = getAuth().currentUser
        const multiFactorSession = await multiFactor(currentUser).getSession()
        this.secret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession)
        this.otpUri = this.secret.generateQrCodeUrl(currentUser.email, issuer)
        this.qrDataUrl = this.renderQrCode(this.otpUri)
      } catch (error) {
        this.pushAlertMessage({
          message: error.message
        })
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    // Rendered locally so the shared secret never leaves the browser
    renderQrCode(uri) {
      const qr = qrcode(0, 'M')
      qr.addData(uri)
      qr.make()
      return qr.createDataURL(5, 2)
    },
    async enroll() {
      this.loading = true
      try {
        const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(this.secret, this.verificationCode)
        await multiFactor(getAuth().currentUser).enroll(multiFactorAssertion, 'Authenticator app')

        this.pushAlertMessage({
          message: 'Authenticator app enrolled successfully for TOTP MFA! Please re-login', type: 'info'
        })
        setTimeout(async () => {
          await this.logout()
          this.$router.push({ name: 'sign-in' })
        }, 3000)
      } catch (error) {
        this.pushAlertMessage({
          message: 'Failed to register TOTP MFA'
        })
        console.error(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
